import { CircleInfoIcon } from "@xola/icons";
import { flash, SubmitButton, Tooltip } from "@xola/ui-kit";
import { isScheduled, requiredString, SwitchField } from "common";
import React from "react";
import { useMutation } from "react-query";
import {
    CancellationProtectionPreference,
    CutoffStrategy,
    Experience,
    PaymentMethod,
    PriceDecreasePolicy,
    PriceIncreasePolicy,
    TravelerPreference,
} from "types";
import { array, boolean, lazy, mixed, number, object, string } from "yup";
import { api } from "../../../api/api";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { CancellationPreferenceField } from "./CancellationPreferenceField";
import { PriceChangePolicyField } from "./PriceChangePolicyField";
import { ReschedulePreferenceField } from "./ReschedulePreferenceField";

interface ExperienceSelfServiceProps {
    experience: Experience;
}

interface SelfServiceFormValues extends TravelerPreference {
    cancellationProtection: CancellationProtectionPreference;
}

export const ExperienceSelfService = ({ experience }: ExperienceSelfServiceProps) => {
    const invalidate = useInvalidate();
    const hasSchedules = isScheduled(experience);
    const isCancellationProtectionEligible = experience.cancellationProtection?.eligible ?? false;

    const { mutateAsync: updateTravelerPreference, isLoading: isSavingTravelerPreference } = useMutation(
        async (payload: TravelerPreference) => {
            return api.post(`/experiences/${experience.id}/travelerPreference`, payload);
        },
    );

    const { mutateAsync: updateCancellationProtection, isLoading: isSavingCancellationProtection } = useMutation(
        async (enabled: boolean) => {
            return api.post(`/experiences/${experience.id}/cancellationProtection`, { enabled });
        },
    );

    const isSaving = isSavingTravelerPreference || isSavingCancellationProtection;

    const handleSubmit = async (payload: SelfServiceFormValues) => {
        // `cancellationProtection` is a separate embedded document with its own endpoint — keeping it out of the
        // travelerPreference body also stops the Xola-controlled `eligible` flag round-tripping through a seller write.
        const { cancellationProtection, ...travelerPreference } = payload;

        await Promise.all([
            updateTravelerPreference(travelerPreference),
            ...(isCancellationProtectionEligible ? [updateCancellationProtection(cancellationProtection.enabled)] : []),
        ]);

        flash.success("Successfully updated experience");
        invalidate("experiences", experience.id);
    };

    return (
        <ContentLayout isWide isDisabled={isSaving}>
            <XolaFormik
                validateOnChange={false}
                validationSchema={getValidationSchema()}
                initialValues={getInitialValues(experience)}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, setFieldError, handleSubmit, handleReset, errors }) => {
                    return (
                        <>
                            <h2>Self-Service</h2>
                            <div className="divide-y divide-gray-light">
                                <ContentLayout.Section
                                    title="Booking Modifications"
                                    description="These settings let you permit your customers to make changes to their own reservations"
                                >
                                    <div className="flex flex-col space-y-2">
                                        <div className="flex items-center space-x-1">
                                            <SwitchField
                                                isChecked={values.questionnaire.value}
                                                onChange={(value: boolean) =>
                                                    setFieldValue("questionnaire.value", value)
                                                }
                                            >
                                                Permit questionnaire changes
                                            </SwitchField>
                                            <Tooltip content="Add-ons configured to be part of questionnaire cannot be modified by customers via the self service portal.">
                                                <CircleInfoIcon />
                                            </Tooltip>
                                        </div>

                                        {hasSchedules ? (
                                            <ReschedulePreferenceField
                                                value={values.reschedule}
                                                errors={errors?.reschedule}
                                                onChange={(value) => setFieldValue("reschedule", value)}
                                            />
                                        ) : null}

                                        {values.reschedule !== null && hasSchedules ? <PriceChangePolicyField /> : null}

                                        <SwitchField
                                            isChecked={values.allowPayment.value}
                                            onChange={(value: boolean) => setFieldValue("allowPayment.value", value)}
                                        >
                                            Allow customers to make payment
                                        </SwitchField>
                                    </div>
                                </ContentLayout.Section>

                                <ContentLayout.Section
                                    title="Cancellation Refund Policy"
                                    description="Configure the refund policy for customer-initiated cancellations"
                                >
                                    <CancellationPreferenceField
                                        experience={experience}
                                        value={values.cancellation}
                                        errors={errors?.cancellation}
                                        onChange={(value) => {
                                            setFieldValue("cancellation", value);
                                            if (value?.paymentMethods?.length) {
                                                setFieldError("cancellation.paymentMethods", undefined);
                                            }
                                        }}
                                    />
                                </ContentLayout.Section>

                                {isCancellationProtectionEligible && (
                                    <ContentLayout.Section
                                        title="Cancellation Protection"
                                        description="Offer cancellation protection on this experience at checkout"
                                    >
                                        <div data-testid="experience-cancellation-protection-section">
                                            <SwitchField
                                                isChecked={values.cancellationProtection.enabled}
                                                onChange={(value: boolean) =>
                                                    setFieldValue("cancellationProtection.enabled", value)
                                                }
                                            >
                                                Offer cancellation protection at checkout
                                            </SwitchField>
                                        </div>
                                    </ContentLayout.Section>
                                )}

                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton
                                        isLoading={isSaving}
                                        type="submit"
                                        size="medium"
                                        onClick={handleSubmit}
                                    >
                                        Save
                                    </SubmitButton>
                                    <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
                                </ContentLayout.Footer>
                            </div>

                            <ScrollToFieldError />
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

const getInitialValues = (experience: Experience) => {
    const { travelerPreference } = experience;
    const hasSchedules = isScheduled(experience);

    const defaultValue = {
        questionnaire: { value: false },
        allowPayment: { value: false },
        cancellation: null,
        reschedule: null,
    };

    const initialValues = {
        ...defaultValue,
        ...travelerPreference,
        cancellationProtection: experience.cancellationProtection ?? { eligible: false, enabled: false },
    };

    /** If there are no schedules we need to default with Arrival time */

    if (
        (initialValues.cancellation && !initialValues.cancellation.cutoff.strategy) ||
        (!hasSchedules && initialValues.cancellation?.cutoff.strategy === CutoffStrategy.CHECK_IN)
    ) {
        initialValues.cancellation.cutoff.strategy = CutoffStrategy.ARRIVAL;
    }

    if (
        (initialValues.reschedule && !initialValues.reschedule.cutoff.strategy) ||
        (!hasSchedules && initialValues.reschedule?.cutoff.strategy === CutoffStrategy.CHECK_IN)
    ) {
        initialValues.reschedule.cutoff.strategy = CutoffStrategy.ARRIVAL;
    }

    if (initialValues.reschedule && !initialValues.reschedule.priceChangePolicy) {
        initialValues.reschedule.priceChangePolicy = {
            priceIncreasePolicy: PriceIncreasePolicy.PAYMENT_BEFORE_MODIFICATION,
            priceDecreasePolicy: PriceDecreasePolicy.REFUND_DELTA,
        };
    }

    return initialValues;
};

const getValidationSchema = () => {
    const cutoffValidation = object().shape({
        duration: number().min(0, "Must be a positive integer"),
        strategy: requiredString(),
    });

    return object().shape({
        questionnaire: object().shape({ value: boolean().required() }),
        allowPayment: object().shape({ value: boolean().required() }),
        cancellationProtection: object().shape({ enabled: boolean().required() }),
        reschedule: lazy((value) => {
            if (value) {
                return object().shape({
                    cutoff: cutoffValidation,
                    priceChangePolicy: object().shape({
                        priceIncreasePolicy: mixed().oneOf(Object.values(PriceIncreasePolicy)).required(),
                        priceDecreasePolicy: mixed().oneOf(Object.values(PriceDecreasePolicy)).required(),
                    }),
                });
            }

            return mixed();
        }),
        cancellation: lazy((value) => {
            if (value) {
                return object().shape({
                    cutoff: cutoffValidation,
                    refundPercentage: number()
                        .typeError("Must be a positive integer")
                        .required("Must be a positive integer")
                        .min(0, "Must be a number between 0 and 100")
                        .max(100, "Must be a number between 0 and 100"),
                    paymentMethods: array()
                        .of(string().oneOf([PaymentMethod.CREDIT_CARD, PaymentMethod.STORE_CREDIT]))
                        .test("atLeastOneMethod", "At least one payment method must be selected", (value, context) => {
                            const refundPercentage = context.parent.refundPercentage;
                            if (refundPercentage > 0) {
                                return Array.isArray(value) && value.length > 0;
                            }
                            return true;
                        }),
                });
            }

            return mixed();
        }),
    });
};
